/**
 * The typed HTTP client. Every network call in the console goes through here.
 *
 * WHY IT IMPORTS THE SERVER'S CONTRACT
 * src/api/contract.ts calls itself law for both sides, and this file takes that literally:
 * the response types below are the server's own types, imported across the directory
 * boundary (tsconfig.json includes ../src/api/contract.ts). Renaming a field on the server
 * turns this file red rather than turning a panel blank at 2 AM.
 *
 * The `PATHS` map is checked against the contract's ROUTES at compile time — `satisfies
 * Record<RouteKey, string>` means adding a route to the contract without adding it here,
 * or keeping one here after the server dropped it, is a build failure. ROUTES itself is
 * imported type-only, so nothing from the server bundle ships to the browser.
 *
 * ERROR SHAPE
 * The API answers { error, detail } with a real status (src/api/index.ts). ApiError below
 * carries all three plus the status, and `ApiError.message` is the server's `detail` when
 * there is one — those details are written for operators ("Try again in about 2 minutes,
 * or ask an owner to reset your password") and are almost always better than anything the
 * console could invent. Doctrine: errors never blame the user and always say what next.
 */
import type {
  AddNumberRequest,
  BillingResponse,
  BrandItem,
  CallDetail,
  CallListItem,
  ConnectIntegrationRequest,
  CreateFlowRequest,
  DeclareOutageRequest,
  EscalationDetail,
  EscalationSummary,
  FlowDetail,
  FlowGraph,
  FlowIssue,
  FlowKind,
  FlowListItem,
  FlowPalette,
  FlowsResponse,
  FlowTemplateCard,
  ChooseVoiceResponse,
  DeliveryRange,
  VoiceDelivery,
  VoiceOption,
  VoicesResponse,
  HelpTip,
  InboxItem,
  IntegrationItem,
  IntegrationsResponse,
  Iso,
  ManualPage,
  MeResponse,
  NmsConnectionStatus,
  OncallResponse,
  OncallContact,
  OncallEligibleUser,
  OncallHours,
  OncallPolicy,
  AddOncallContactRequest,
  PatchOncallContactRequest,
  ReorderOncallRequest,
  PatchOncallHoursRequest,
  OutageListResponse,
  OutageSummary,
  Page,
  PhoneNumberItem,
  PlaybookItem,
  PolicyResponse,
  ProposalItem,
  ReportsResponse,
  ROUTES,
  RunFlowResponse,
  SaveFlowRequest,
  SetupResponse,
  TonightResponse,
  UserItem,
} from '../../../src/api/contract.ts';
import type { Disposition, Role } from '../../../src/types.ts';

export type {
  AddNumberRequest,
  BillingResponse,
  BrandItem,
  CallDetail,
  CallListItem,
  ConnectIntegrationRequest,
  CreateFlowRequest,
  DeclareOutageRequest,
  EscalationDetail,
  EscalationSummary,
  FlowDetail,
  FlowGraph,
  FlowKind,
  FlowListItem,
  FlowPalette,
  FlowsResponse,
  FlowTemplateCard,
  DeliveryRange,
  VoiceDelivery,
  VoiceOption,
  VoicesResponse,
  HealthDot,
  HelpTip,
  InboxItem,
  IntegrationItem,
  IntegrationsResponse,
  Iso,
  LiveCall,
  ManualPage,
  MeResponse,
  NmsConnectionStatus,
  OncallResponse,
  OncallContact,
  OncallEligibleUser,
  OncallHours,
  OncallPolicy,
  AddOncallContactRequest,
  PatchOncallContactRequest,
  ReorderOncallRequest,
  PatchOncallHoursRequest,
  OutageListResponse,
  OutageSummary,
  Page,
  PhoneNumberItem,
  PlaybookItem,
  PlaybookStep,
  PolicyResponse,
  ProposalItem,
  ReportsResponse,
  RunFlowResponse,
  SaveFlowRequest,
  SetupResponse,
  SetupStep,
  TonightResponse,
  TranscriptTurn,
  UserItem,
} from '../../../src/api/contract.ts';
/**
 * Domain scalars and enums live in src/types.ts, not in the contract — contract.ts imports
 * them and does not re-export. Screens should take them from HERE so there is one import
 * path for "console types" and nobody has to remember which file a name lives in.
 */
export type { AiMode, Disposition, E164, Permission, Role, StandingFlag, Uuid } from '../../../src/types.ts';

// ---------------------------------------------------------------------- error type

export class ApiError extends Error {
  readonly status: number;
  /** The machine code from the API: 'forbidden', 'invalid_request', 'conflict', ... */
  readonly code: string;
  /** The operator-facing sentence, when the server sent one. */
  readonly detail: string | null;
  readonly url: string;

  constructor(input: { status: number; code: string; detail?: string | null; url: string }) {
    super(input.detail || defaultMessage(input.status, input.code));
    this.name = 'ApiError';
    this.status = input.status;
    this.code = input.code;
    this.detail = input.detail ?? null;
    this.url = input.url;
  }

  /** True for the failures that are worth a retry button rather than an explanation. */
  get retryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * Fallbacks for when the server did not send a detail. Every one of them says what the
 * operator should do next; none of them says "invalid input" and stops.
 */
function defaultMessage(status: number, code: string): string {
  switch (status) {
    case 0:
      return 'The console could not reach NightDesk. Check the connection and try again — calls are still being answered.';
    case 401:
      return 'Your session ended. Sign in again to continue.';
    case 403:
      return 'Your role does not allow that. Ask an owner or admin to do it, or to change your role.';
    case 404:
      return 'That record is gone, or it belongs to another workspace.';
    case 409:
      return 'Somebody else changed this first. Reload to see the current state, then try again.';
    case 422:
      return 'That change was refused. Check the highlighted fields and try again.';
    case 429:
      return 'Too many requests in a row. Wait a moment and try again.';
    case 503:
      return 'NightDesk is starting up or a dependency is down. Try again in a minute.';
    default:
      return status >= 500
        ? 'Something on our side failed. Try again; if it keeps happening, send us the time it occurred.'
        : `The request failed (${code || status}).`;
  }
}

// ---------------------------------------------------------------------- session hook

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * App.tsx registers a handler so a 401 becomes a router navigation (which preserves the
 * page the operator was on) instead of a hard reload. Before it registers, and if it ever
 * throws, we fall back to a location change — being logged out must never leave a console
 * sitting on a screen it cannot refresh.
 */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

let redirecting = false;

function handleUnauthorized(): void {
  if (redirecting) return;
  if (window.location.pathname === '/login') return;
  redirecting = true;
  // Cleared on the next successful request; a stuck flag would swallow a later 401.
  setTimeout(() => {
    redirecting = false;
  }, 1500);
  try {
    if (onUnauthorized) {
      onUnauthorized();
      return;
    }
  } catch {
    /* fall through to the hard redirect */
  }
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

// ---------------------------------------------------------------------- route table

type RouteKey = keyof typeof ROUTES;

/**
 * Path templates, one per contract route, in contract order. `satisfies` makes the
 * key set exact: a route added to ROUTES and not here fails to compile, and so does a
 * path here that ROUTES no longer declares.
 */
const PATHS = {
  login: '/api/auth/login',
  logout: '/api/auth/logout',
  me: '/api/me',
  setup: '/api/setup',
  tonight: '/api/tonight',
  calls: '/api/calls',
  callDetail: '/api/calls/:id',
  flagCall: '/api/calls/:id/flag',
  outages: '/api/outages',
  declareOutage: '/api/outages',
  updateOutage: '/api/outages/:id',
  resolveOutage: '/api/outages/:id/resolve',
  armStorm: '/api/storm/arm',
  disarmStorm: '/api/storm/disarm',
  nmsConnections: '/api/nms',
  escalations: '/api/escalations',
  escalationDetail: '/api/escalations/:id',
  ackEscalation: '/api/escalations/:id/ack',
  resolveEscalation: '/api/escalations/:id/resolve',
  proposals: '/api/proposals',
  decideProposal: '/api/proposals/:id/decide',
  inbox: '/api/inbox',
  resolveInboxItem: '/api/inbox/:id/resolve',
  policy: '/api/policy',
  updatePolicy: '/api/policy',
  playbooks: '/api/playbooks',
  savePlaybook: '/api/playbooks/:id',
  flows: '/api/flows',
  flow: '/api/flows/:id',
  flowTemplates: '/api/flows/templates',
  createFlow: '/api/flows',
  saveFlow: '/api/flows/:id',
  pinFlow: '/api/flows/:id/pin',
  reorderFlows: '/api/flows/reorder',
  runFlow: '/api/flows/:id/run',
  retireFlow: '/api/flows/:id',
  voices: '/api/voices',
  voiceSample: '/api/voices/sample',
  chooseVoice: '/api/voices',
  integrations: '/api/integrations',
  connectIntegration: '/api/integrations',
  verifyIntegration: '/api/integrations/:id/verify',
  revokeIntegration: '/api/integrations/:id',
  billing: '/api/billing',
  reports: '/api/reports',
  users: '/api/users',
  brands: '/api/brands',
  numbers: '/api/numbers',
  addNumber: '/api/numbers',
  deleteNumber: '/api/numbers/:id',
  oncall: '/api/oncall',
  addOncallContact: '/api/oncall/contacts',
  patchOncallContact: '/api/oncall/contacts/:id',
  deleteOncallContact: '/api/oncall/contacts/:id',
  reorderOncall: '/api/oncall/reorder',
  patchOncallHours: '/api/oncall/hours',
  manual: '/api/manual',
  manualPage: '/api/manual/:slug',
  help: '/api/help',
} satisfies Record<RouteKey, string>;

/**
 * User administration is implemented in src/api/settings.ts but is not (yet) named in the
 * contract's ROUTES map. Kept apart from PATHS so the `satisfies` check above stays exact,
 * and so the day these are added to the contract the move is obvious.
 */
const EXTRA_PATHS = {
  inviteUser: '/api/users',
  updateUser: '/api/users/:id',
  resetPassword: '/api/users/:id/password',
} as const;

function fill(template: string, params: Record<string, string>): string {
  return template.replace(/:([a-zA-Z]+)/g, (_m, key: string) => {
    const value = params[key];
    if (value === undefined) throw new Error(`api: missing path parameter ":${key}" for ${template}`);
    return encodeURIComponent(value);
  });
}

type QueryValue = string | number | boolean | null | undefined;

function qs(params: Record<string, QueryValue> | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------- transport

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

type RequestOptions = {
  /** Set on the login call so its 401 renders inline instead of bouncing to /login. */
  skipAuthRedirect?: boolean;
  signal?: AbortSignal;
};

async function request<T>(
  method: Method,
  url: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      // Same origin in production; the dev proxy keeps it same-origin too.
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        // Lets the API tell a console call from a webhook in the log without sniffing UA.
        'X-Requested-With': 'nightdesk-console',
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    // Offline, DNS, TLS, or the operator closed the laptop. Status 0 means "we never got
    // an answer", which is a different sentence from "the server said no".
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError({ status: 0, code: 'network_unreachable', url });
  }

  if (response.status === 401 && !options.skipAuthRedirect) {
    handleUnauthorized();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const shape = (payload ?? {}) as { error?: unknown; detail?: unknown };
    throw new ApiError({
      status: response.status,
      code: typeof shape.error === 'string' ? shape.error : `http_${response.status}`,
      detail: typeof shape.detail === 'string' ? shape.detail : null,
      url,
    });
  }

  return payload as T;
}

const get = <T>(url: string, options?: RequestOptions): Promise<T> => request<T>('GET', url, undefined, options);
const post = <T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> =>
  request<T>('POST', url, body ?? {}, options);
const patch = <T>(url: string, body: unknown): Promise<T> => request<T>('PATCH', url, body);
const put = <T>(url: string, body: unknown): Promise<T> => request<T>('PUT', url, body);
const del = <T>(url: string): Promise<T> => request<T>('DELETE', url);

// ---------------------------------------------------------------------- request shapes
//
// Bodies the routers accept but the contract does not name a type for. They mirror the zod
// schemas in src/api/* exactly; if one of these drifts the route answers 400 with a real
// sentence rather than failing silently, but keep them in step.

export type CallsQuery = {
  q?: string;
  disposition?: Disposition;
  escalated?: boolean;
  storm?: boolean;
  brand?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
};

/** src/api/calls.ts flagBody — reason is required, 2-60 chars. */
export type FlagCallRequest = { reason: string; note?: string };

/** src/api/outages.ts updateSchema. `etaText: null` clears the ETA. */
export type UpdateOutageRequest = {
  label?: string;
  scope?: string;
  bannerText?: string;
  etaText?: string | null;
  affectedCount?: number | null;
  status?: 'suspected' | 'confirmed' | 'restoring';
  expiresInHours?: number;
  notifyOnRestore?: boolean;
  /** "Still down" without changing anything else — pushes the re-confirm clock. */
  reconfirm?: boolean;
};

export type ResolveOutageRequest = { note?: string; notifyRestore?: boolean };
export type ArmStormRequest = { hours?: number };

export type EscalationsQuery = { state?: string; limit?: number; cursor?: string };
export type ResolveEscalationRequest = { note?: string };

export type ProposalsQuery = {
  status?: 'pending' | 'decided' | 'all';
  kind?: string;
  limit?: number;
  cursor?: string;
};

/**
 * approve_always / reject_never also write the standing policy change, which is why the
 * console must label those two differently from a one-off decision (doctrine 2: a gated
 * action that becomes permanent has to say so before it is clicked).
 */
export type ProposalDecision = 'approve' | 'approve_always' | 'reject' | 'reject_never';
export type DecideProposalRequest = { decision: ProposalDecision; note?: string };

export type InboxQuery = {
  kind?: 'ticket' | 'message' | 'lead';
  status?: 'open' | 'resolved' | 'all';
  limit?: number;
  cursor?: string;
};
export type ResolveInboxRequest = { kind?: 'ticket' | 'message' | 'lead'; note?: string };

/** src/api/policy.ts policyPatchSchema. Read-only fields are refused if changed. */
export type PolicyPatchRequest = Partial<
  Pick<
    PolicyResponse,
    'ticketWrite' | 'restoreSms' | 'callbackPromise' | 'standbyMode' | 'bargeIn' | 'emergencyKeywords' | 'businessHours' | 'retentionDaysRecordings' | 'greeting'
  >
> & {
  speakableFacts?: Partial<PolicyResponse['speakableFacts']>;
  verification?: Partial<PolicyResponse['verification']>;
};

export type SavePlaybookRequest = Pick<PlaybookItem, 'name' | 'trigger' | 'active' | 'steps'> & {
  /** Optimistic concurrency: send back the version you read. */
  version?: number;
};

export type InviteUserRequest = {
  email: string; name: string; role: Role;
  /** Set → create the account ACTIVE with this password. Omit → send an invite. */
  password?: string;
  oncallEligible?: boolean;
  phone?: string;
};
export type UpdateUserRequest = {
  name?: string; role?: Role; disabled?: boolean;
  oncallEligible?: boolean;
  /** null clears the number. */
  phone?: string | null;
};
export type ResetPasswordRequest = { password: string };

// ---------------------------------------------------------------------- endpoints

/**
 * A stored trigger is "kind:ref" — "intent:service_down", "condition:storm_mode". This
 * turns it into a sentence an owner reads at a glance. The server keeps the raw form; this
 * is display only.
 */
export function flowTriggerLabel(trigger: string): string {
  const [kind, ...rest] = trigger.split(':');
  const words = (rest.join(':') || kind || '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (!words) return trigger;
  if (kind === 'condition') return `When ${words} is on`;
  if (kind === 'intent') return `When a caller means “${words}”`;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const api = {
  // ------------------------------------------------------------------ session
  /**
   * Login answers the full MeResponse, not a token — the console renders its first frame
   * from this one response instead of flashing an empty shell while /api/me lands.
   * skipAuthRedirect keeps a wrong password on the login screen.
   */
  login: (body: { email: string; password: string }): Promise<MeResponse> =>
    post<MeResponse>(PATHS.login, body, { skipAuthRedirect: true }),

  logout: (): Promise<{ ok: true }> => post<{ ok: true }>(PATHS.logout, {}, { skipAuthRedirect: true }),

  me: (options?: RequestOptions): Promise<MeResponse> =>
    get<MeResponse>(PATHS.me, { skipAuthRedirect: true, ...options }),

  // ------------------------------------------------------------------ tonight
  setup: (options?: RequestOptions): Promise<SetupResponse> => get<SetupResponse>(PATHS.setup, options),
  tonight: (options?: RequestOptions): Promise<TonightResponse> => get<TonightResponse>(PATHS.tonight, options),

  // ------------------------------------------------------------------ calls
  calls: (query?: CallsQuery): Promise<Page<CallListItem>> =>
    get<Page<CallListItem>>(PATHS.calls + qs(query as Record<string, QueryValue>)),

  call: (id: string): Promise<CallDetail> => get<CallDetail>(fill(PATHS.callDetail, { id })),

  flagCall: (id: string, body: FlagCallRequest): Promise<CallDetail> =>
    post<CallDetail>(fill(PATHS.flagCall, { id }), body),

  // ------------------------------------------------------------------ outages / storm
  outages: (): Promise<OutageListResponse> => get<OutageListResponse>(PATHS.outages),

  declareOutage: (body: DeclareOutageRequest): Promise<OutageSummary> =>
    post<OutageSummary>(PATHS.declareOutage, body),

  updateOutage: (id: string, body: UpdateOutageRequest): Promise<OutageSummary> =>
    patch<OutageSummary>(fill(PATHS.updateOutage, { id }), body),

  resolveOutage: (id: string, body: ResolveOutageRequest = {}): Promise<OutageSummary> =>
    post<OutageSummary>(fill(PATHS.resolveOutage, { id }), body),

  armStorm: (body: ArmStormRequest = {}): Promise<{ stormArmed: boolean; stormArmedUntil: Iso | null }> =>
    post(PATHS.armStorm, body),

  disarmStorm: (): Promise<{ stormArmed: boolean; stormArmedUntil: Iso | null }> => post(PATHS.disarmStorm, {}),

  /** Monitoring connections behind the outage engine. Vendor names live in the adapter. */
  nmsConnections: (): Promise<Page<NmsConnectionStatus>> => get<Page<NmsConnectionStatus>>(PATHS.nmsConnections),

  // ------------------------------------------------------------------ escalations
  escalations: (query?: EscalationsQuery): Promise<Page<EscalationSummary>> =>
    get<Page<EscalationSummary>>(PATHS.escalations + qs(query as Record<string, QueryValue>)),

  escalation: (id: string): Promise<EscalationDetail> =>
    get<EscalationDetail>(fill(PATHS.escalationDetail, { id })),

  /**
   * "I've got this." Stops the paging ladder. Takes no body and no note on purpose —
   * demanding paperwork before the paging stops is how the paging does not stop. Safe to
   * repeat: the server returns the existing acknowledgement rather than an error.
   */
  ackEscalation: (id: string): Promise<EscalationSummary> =>
    post<EscalationSummary>(fill(PATHS.ackEscalation, { id })),

  resolveEscalation: (id: string, body: ResolveEscalationRequest = {}): Promise<EscalationDetail> =>
    post<EscalationDetail>(fill(PATHS.resolveEscalation, { id }), body),

  // ------------------------------------------------------------------ proposals / inbox
  proposals: (query?: ProposalsQuery): Promise<Page<ProposalItem>> =>
    get<Page<ProposalItem>>(PATHS.proposals + qs(query as Record<string, QueryValue>)),

  decideProposal: (id: string, body: DecideProposalRequest): Promise<ProposalItem> =>
    post<ProposalItem>(fill(PATHS.decideProposal, { id }), body),

  inbox: (query?: InboxQuery): Promise<Page<InboxItem>> =>
    get<Page<InboxItem>>(PATHS.inbox + qs(query as Record<string, QueryValue>)),

  resolveInboxItem: (id: string, body: ResolveInboxRequest = {}): Promise<InboxItem> =>
    post<InboxItem>(fill(PATHS.resolveInboxItem, { id }), body),

  // ------------------------------------------------------------------ policy / playbooks
  policy: (): Promise<PolicyResponse> => get<PolicyResponse>(PATHS.policy),

  updatePolicy: (body: PolicyPatchRequest): Promise<PolicyResponse> => patch<PolicyResponse>(PATHS.updatePolicy, body),

  playbooks: (): Promise<Page<PlaybookItem>> => get<Page<PlaybookItem>>(PATHS.playbooks),

  savePlaybook: (id: string, body: SavePlaybookRequest): Promise<PlaybookItem> =>
    put<PlaybookItem>(fill(PATHS.savePlaybook, { id }), body),

  // ------------------------------------------------------------------ flows (the canvas)
  flows: {
    list: (): Promise<FlowsResponse> => get<FlowsResponse>(PATHS.flows),

    get: (id: string): Promise<{ item: FlowDetail; palette: FlowPalette }> =>
      get<{ item: FlowDetail; palette: FlowPalette }>(fill(PATHS.flow, { id })),

    templates: (): Promise<{ templates: FlowTemplateCard[] }> =>
      get<{ templates: FlowTemplateCard[] }>(PATHS.flowTemplates),

    create: (body: CreateFlowRequest): Promise<{ item: FlowDetail }> =>
      post<{ item: FlowDetail }>(PATHS.createFlow, body),

    save: (id: string, body: SaveFlowRequest): Promise<{ item: FlowDetail; warnings?: FlowIssue[] }> =>
      put<{ item: FlowDetail; warnings?: FlowIssue[] }>(fill(PATHS.saveFlow, { id }), body),

    pin: (
      id: string,
      body: { pinned: boolean; pinLabel?: string | null; pinIcon?: string | null; pinOrder?: number },
    ): Promise<{ item: FlowListItem }> => post<{ item: FlowListItem }>(fill(PATHS.pinFlow, { id }), body),

    reorder: (ids: string[]): Promise<{ ok: true }> => post<{ ok: true }>(PATHS.reorderFlows, { ids }),

    run: (id: string, body: { minutes?: number; release?: boolean } = {}): Promise<RunFlowResponse> =>
      post<RunFlowResponse>(fill(PATHS.runFlow, { id }), body),

    retire: (id: string): Promise<{ ok: true }> => del<{ ok: true }>(fill(PATHS.retireFlow, { id })),
  },

  // ------------------------------------------------------------------ voice
  voices: {
    list: (): Promise<VoicesResponse> => get<VoicesResponse>(PATHS.voices),

    choose: (voiceId: string, delivery?: Partial<VoiceDelivery>): Promise<ChooseVoiceResponse> =>
      put<ChooseVoiceResponse>(PATHS.chooseVoice, { voiceId, ...(delivery ? { delivery } : {}) }),

    /**
     * Auditions wording aloud. The route answers with audio bytes, not JSON — the browser
     * cannot play a cross-origin provider clip under the CSP, so the sample comes back
     * through our own origin and is turned into an object URL the caller must revoke when
     * done. `text` lets a mode dialog hear a sentence it has not saved yet.
     */
    sample: async (voiceId?: string, delivery?: Partial<VoiceDelivery>, text?: string): Promise<string> => {
      const res = await fetch(PATHS.voiceSample, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'nightdesk-console' },
        body: JSON.stringify({
          ...(voiceId ? { voiceId } : {}),
          ...(delivery ? { delivery } : {}),
          ...(text ? { text } : {}),
        }),
      });
      if (!res.ok) throw new Error('The voice preview could not be produced.');
      return URL.createObjectURL(await res.blob());
    },
  },

  // ------------------------------------------------------------------ integrations
  integrations: (): Promise<IntegrationsResponse> => get<IntegrationsResponse>(PATHS.integrations),

  connectIntegration: (body: ConnectIntegrationRequest): Promise<IntegrationItem> =>
    post<IntegrationItem>(PATHS.connectIntegration, body),

  verifyIntegration: (id: string): Promise<IntegrationItem> =>
    post<IntegrationItem>(fill(PATHS.verifyIntegration, { id }), {}),

  revokeIntegration: (id: string): Promise<{ ok: true } | IntegrationItem> =>
    del(fill(PATHS.revokeIntegration, { id })),

  // ------------------------------------------------------------------ billing / reports
  billing: (): Promise<BillingResponse> => get<BillingResponse>(PATHS.billing),

  reports: (days?: number): Promise<ReportsResponse> => get<ReportsResponse>(PATHS.reports + qs({ days })),

  // ------------------------------------------------------------------ settings
  users: (): Promise<Page<UserItem>> => get<Page<UserItem>>(PATHS.users),

  inviteUser: (body: InviteUserRequest): Promise<UserItem> => post<UserItem>(EXTRA_PATHS.inviteUser, body),

  updateUser: (id: string, body: UpdateUserRequest): Promise<UserItem> =>
    patch<UserItem>(fill(EXTRA_PATHS.updateUser, { id }), body),
  resetPassword: (id: string, body: ResetPasswordRequest): Promise<{ ok: true }> =>
    post<{ ok: true }>(fill(EXTRA_PATHS.resetPassword, { id }), body),

  brands: (): Promise<Page<BrandItem>> => get<Page<BrandItem>>(PATHS.brands),

  numbers: (): Promise<Page<PhoneNumberItem>> => get<Page<PhoneNumberItem>>(PATHS.numbers),
  addNumber: (body: AddNumberRequest): Promise<{ ok: true; id: string }> => post<{ ok: true; id: string }>(PATHS.addNumber, body),
  deleteNumber: (id: string): Promise<{ ok: true }> => del<{ ok: true }>(fill(PATHS.deleteNumber, { id })),

  // ------------------------------------------------------------------ on-call & escalation
  oncall: (options?: RequestOptions): Promise<OncallResponse> => get<OncallResponse>(PATHS.oncall, options),
  addOncallContact: (body: AddOncallContactRequest): Promise<OncallResponse> =>
    post<OncallResponse>(PATHS.addOncallContact, body),
  patchOncallContact: (id: string, body: PatchOncallContactRequest): Promise<OncallResponse> =>
    patch<OncallResponse>(fill(PATHS.patchOncallContact, { id }), body),
  deleteOncallContact: (id: string): Promise<OncallResponse> =>
    del<OncallResponse>(fill(PATHS.deleteOncallContact, { id })),
  reorderOncall: (ids: string[]): Promise<OncallResponse> =>
    post<OncallResponse>(PATHS.reorderOncall, { ids } satisfies ReorderOncallRequest),
  patchOncallHours: (body: PatchOncallHoursRequest): Promise<OncallResponse> =>
    patch<OncallResponse>(PATHS.patchOncallHours, body),

  // ------------------------------------------------------------------ manual / help
  manual: (): Promise<Page<ManualPage>> => get<Page<ManualPage>>(PATHS.manual),

  manualPage: (slug: string): Promise<ManualPage> => get<ManualPage>(fill(PATHS.manualPage, { slug })),

  help: (): Promise<Page<HelpTip>> => get<Page<HelpTip>>(PATHS.help),
} as const;

export { PATHS as API_PATHS };
export default api;
